/**
 * Workspace Action Functions - Fetch and mutation operations for Workspaces
 *
 * These functions handle all workspace operations including members management.
 */

import type { CreateWorkspaceBody, UpdateWorkspaceBody } from "@/isomorphic/schemas";
import { getAtomStore } from "@lib/atoms";
import { API_ROUTES } from "@/frontend/lib/api/routes";
import {
  _apiWorkspacesAtom,
  workspacesStatusAtom,
  workspacesErrorAtom,
  currentWorkspaceAtom,
  currentWorkspaceStatusAtom,
  currentWorkspaceErrorAtom,
  currentWorkspaceVersionAtom,
  workspaceMembersAtom,
  workspaceMembersStatusAtom,
  workspaceMembersErrorAtom,
  workspaceInvitationsAtom,
  workspaceInvitationsStatusAtom,
  workspaceInvitationsErrorAtom,
} from "../core/workspaces";

// ============================================================================
// WORKSPACE FETCH FUNCTIONS
// ============================================================================

/**
 * Initialize user workspaces
 */
export const initUserWorkspaces = async (jwt: string) => {
  const store = getAtomStore();
  try {
    store.set(workspacesStatusAtom, "loading");
    store.set(workspacesErrorAtom, null);

    const response = await fetch(API_ROUTES.workspaces.getAll, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${jwt}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const msg = `HTTP error! status: ${response.status}`;
      console.error("[WorkspaceActions]", msg);
      store.set(_apiWorkspacesAtom, []);
      store.set(workspacesStatusAtom, "error");
      store.set(workspacesErrorAtom, msg);
      return;
    }

    const { data, error } = await response.json();

    if (error) {
      console.error("[WorkspaceActions] Error fetching workspaces:", error);
      store.set(_apiWorkspacesAtom, []);
      store.set(workspacesStatusAtom, "error");
      store.set(workspacesErrorAtom, String(error));
      return;
    }

    if (Array.isArray(data)) {
      store.set(_apiWorkspacesAtom, data);
      store.set(workspacesStatusAtom, "success");
      return;
    }

    const msg = "Unexpected response format";
    console.error("[WorkspaceActions]", msg, data);
    store.set(_apiWorkspacesAtom, []);
    store.set(workspacesStatusAtom, "error");
    store.set(workspacesErrorAtom, msg);
  } catch (error) {
    console.error("[WorkspaceActions] Error fetching workspaces:", error);
    store.set(_apiWorkspacesAtom, []);
    store.set(workspacesStatusAtom, "error");
    store.set(workspacesErrorAtom, error instanceof Error ? error.message : String(error));
  }
};

/**
 * Fetch workspace by ID
 */
export const fetchWorkspace = async (jwt: string, workspaceId: string) => {
  const store = getAtomStore();
  try {
    store.set(currentWorkspaceStatusAtom, "loading");
    store.set(currentWorkspaceErrorAtom, null);

    const response = await fetch(API_ROUTES.workspaces.getById(workspaceId), {
      method: "GET",
      headers: {
        Authorization: `Bearer ${jwt}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const msg = `${response.status}:HTTP error! status: ${response.status}`;
      console.error("[WorkspaceActions]", msg);
      store.set(currentWorkspaceAtom, null);
      store.set(currentWorkspaceStatusAtom, "error");
      store.set(currentWorkspaceErrorAtom, msg);
      return;
    }

    const { error, data } = await response.json();

    if (error) {
      console.error("[WorkspaceActions] Error fetching workspace:", error);
      store.set(currentWorkspaceAtom, null);
      store.set(currentWorkspaceStatusAtom, "error");
      store.set(currentWorkspaceErrorAtom, String(error));
      return;
    }

    if (typeof data !== "object") {
      const msg = "Invalid response";
      store.set(currentWorkspaceAtom, null);
      store.set(currentWorkspaceStatusAtom, "error");
      store.set(currentWorkspaceErrorAtom, msg);
      return;
    }

    const version = data.version || 0;

    store.set(currentWorkspaceAtom, data);
    store.set(currentWorkspaceVersionAtom, version);
    store.set(currentWorkspaceStatusAtom, "success");

    return { workspaceData: data, version };
  } catch (error) {
    console.error("[WorkspaceActions] Error fetching workspace:", error);
    store.set(currentWorkspaceAtom, null);
    store.set(currentWorkspaceStatusAtom, "error");
    store.set(currentWorkspaceErrorAtom, error instanceof Error ? error.message : String(error));
    return;
  }
};

// ============================================================================
// WORKSPACE MUTATION FUNCTIONS
// ============================================================================

/**
 * Update workspace
 */
export const updateWorkspace = async (
  jwt: string,
  workspaceId: string,
  workspaceData: Partial<UpdateWorkspaceBody>,
  version: number,
) => {
  const store = getAtomStore();
  try {
    const requestBody = {
      ...workspaceData,
      version: version || 1,
    };

    const response = await fetch(API_ROUTES.workspaces.update(workspaceId), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const msg = `HTTP error! status: ${response.status}`;
      console.error("[WorkspaceActions]", msg);
      store.set(currentWorkspaceErrorAtom, msg);
      return null;
    }

    const { data, error } = await response.json();

    if (error) {
      console.error("[WorkspaceActions] Error updating workspace:", error);
      store.set(currentWorkspaceErrorAtom, String(error));
      return null;
    }

    // Update local state
    store.set(currentWorkspaceAtom, data);
    store.set(currentWorkspaceVersionAtom, data.version || 0);

    // Update in workspaces list
    const workspaces = store.get(_apiWorkspacesAtom);
    store.set(
      _apiWorkspacesAtom,
      workspaces.map((w) => (w.id === workspaceId ? data : w)),
    );

    return data;
  } catch (error) {
    console.error("[WorkspaceActions] Error updating workspace:", error);
    store.set(currentWorkspaceErrorAtom, error instanceof Error ? error.message : String(error));
    return null;
  }
};

/**
 * Create workspace
 */
export const createWorkspace = async (jwt: string, workspaceData: Partial<CreateWorkspaceBody>) => {
  const store = getAtomStore();
  try {
    // Build request body, omitting undefined/null values for optional fields
    const requestBody: CreateWorkspaceBody = {
      name: workspaceData.name || "New Workspace",
      ...(workspaceData.description && { description: workspaceData.description }),
      ...(workspaceData.logoPublicUrl && { logoPublicUrl: workspaceData.logoPublicUrl }),
      ...(workspaceData.isVirtual !== undefined && { isVirtual: workspaceData.isVirtual }),
    };

    const response = await fetch(API_ROUTES.workspaces.create, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: "Unknown error" }));
      console.error("[WorkspaceActions] Create workspace failed:", {
        status: response.status,
        error: errorData,
        requestBody,
      });
      return null;
    }

    const { data, error } = await response.json();

    if (error) {
      console.error("[WorkspaceActions] Error creating workspace:", error);
      return null;
    }

    // Add to local state
    const workspaces = store.get(_apiWorkspacesAtom);
    store.set(_apiWorkspacesAtom, [...workspaces, data]);

    return data;
  } catch (error) {
    console.error("[WorkspaceActions] Error creating workspace:", error);
    return null;
  }
};

// ============================================================================
// WORKSPACE MEMBERS FUNCTIONS
// ============================================================================

/**
 * Fetch workspace members
 */
export const fetchWorkspaceMembers = async (jwt: string, workspaceId: string) => {
  const store = getAtomStore();
  try {
    store.set(workspaceMembersStatusAtom, "loading");
    store.set(workspaceMembersErrorAtom, null);

    const response = await fetch(API_ROUTES.admin.workspaces.getMembers(workspaceId), {
      method: "GET",
      headers: {
        Authorization: `Bearer ${jwt}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const msg = `${response.status}:HTTP error! status: ${response.status}`;
      console.error("[WorkspaceActions]", msg);
      store.set(workspaceMembersAtom, []);
      store.set(workspaceMembersStatusAtom, "error");
      store.set(workspaceMembersErrorAtom, msg);
      return;
    }

    const { data, error } = await response.json();

    if (error) {
      console.error("[WorkspaceActions] Error fetching members:", error);
      store.set(workspaceMembersAtom, []);
      store.set(workspaceMembersStatusAtom, "error");
      store.set(workspaceMembersErrorAtom, String(error));
      return;
    }

    if (Array.isArray(data)) {
      store.set(workspaceMembersAtom, data);
      store.set(workspaceMembersStatusAtom, "success");
      return;
    }

    const msg = "Unexpected response format";
    console.error("[WorkspaceActions]", msg, data);
    store.set(workspaceMembersAtom, []);
    store.set(workspaceMembersStatusAtom, "error");
    store.set(workspaceMembersErrorAtom, msg);
  } catch (error) {
    console.error("[WorkspaceActions] Error fetching members:", error);
    store.set(workspaceMembersAtom, []);
    store.set(workspaceMembersStatusAtom, "error");
    store.set(workspaceMembersErrorAtom, error instanceof Error ? error.message : String(error));
  }
};

/**
 * Add workspace member
 */
export const addWorkspaceMember = async (jwt: string, workspaceId: string, email: string, role: string) => {
  const store = getAtomStore();
  try {
    store.set(workspaceMembersErrorAtom, null);
    const response = await fetch(API_ROUTES.admin.workspaces.addMember(workspaceId), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({ email, role }),
    });

    if (!response.ok) {
      const msg = `HTTP error! status: ${response.status}`;
      console.error("[WorkspaceActions]", msg);
      store.set(workspaceMembersErrorAtom, msg);
      return null;
    }

    const { data, error } = await response.json();

    if (error) {
      console.error("[WorkspaceActions] Error adding member:", error);
      store.set(workspaceMembersErrorAtom, String(error));
      return null;
    }

    // Reset to trigger refetch
    store.set(workspaceMembersAtom, []);
    store.set(workspaceMembersStatusAtom, "init");

    return data;
  } catch (error) {
    console.error("[WorkspaceActions] Error adding member:", error);
    store.set(workspaceMembersErrorAtom, error instanceof Error ? error.message : String(error));
    return null;
  }
};

/**
 * Update workspace member role
 */
export const updateWorkspaceMemberRole = async (jwt: string, workspaceId: string, userId: string, role: string) => {
  const store = getAtomStore();
  try {
    store.set(workspaceMembersErrorAtom, null);
    const response = await fetch(API_ROUTES.admin.workspaces.updateMemberRole(workspaceId), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({ target_user_id: userId, role }),
    });

    if (!response.ok) {
      const msg = `HTTP error! status: ${response.status}`;
      console.error("[WorkspaceActions]", msg);
      store.set(workspaceMembersErrorAtom, msg);
      return null;
    }

    const { data, error } = await response.json();

    if (error) {
      console.error("[WorkspaceActions] Error updating member role:", error);
      store.set(workspaceMembersErrorAtom, String(error));
      return null;
    }

    // Reset to trigger refetch
    store.set(workspaceMembersAtom, []);
    store.set(workspaceMembersStatusAtom, "init");

    return data;
  } catch (error) {
    console.error("[WorkspaceActions] Error updating member role:", error);
    store.set(workspaceMembersErrorAtom, error instanceof Error ? error.message : String(error));
    return null;
  }
};

// ============================================================================
// WORKSPACE INVITATIONS FUNCTIONS
// ============================================================================

/**
 * Fetch workspace invitations
 */
export const fetchWorkspaceInvitations = async (jwt: string, workspaceId: string) => {
  const store = getAtomStore();
  try {
    store.set(workspaceInvitationsStatusAtom, "loading");
    store.set(workspaceInvitationsErrorAtom, null);

    const response = await fetch(API_ROUTES.admin.workspaces.invitations(workspaceId), {
      method: "GET",
      headers: {
        Authorization: `Bearer ${jwt}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const msg = `${response.status}:HTTP error! status: ${response.status}`;
      console.error("[WorkspaceActions]", msg);
      store.set(workspaceInvitationsAtom, []);
      store.set(workspaceInvitationsStatusAtom, "error");
      store.set(workspaceInvitationsErrorAtom, msg);
      return;
    }

    const { data } = await response.json();
    store.set(workspaceInvitationsAtom, data.invitations || []);
    store.set(workspaceInvitationsStatusAtom, "success");
  } catch (error) {
    console.error("[WorkspaceActions] Error fetching invitations:", error);
    store.set(workspaceInvitationsAtom, []);
    store.set(workspaceInvitationsStatusAtom, "error");
    store.set(workspaceInvitationsErrorAtom, error instanceof Error ? error.message : String(error));
  }
};

/**
 * Revoke workspace invitation
 */
export const revokeWorkspaceInvitation = async (
  jwt: string,
  workspaceId: string,
  invitationId: string,
  reason?: string,
) => {
  const store = getAtomStore();
  try {
    store.set(workspaceInvitationsErrorAtom, null);
    const response = await fetch(API_ROUTES.admin.workspaces.revokeInvitation(workspaceId), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({ invitationId, reason }),
    });

    if (!response.ok) {
      const msg = `HTTP error! status: ${response.status}`;
      console.error("[WorkspaceActions]", msg);
      store.set(workspaceInvitationsErrorAtom, msg);
      return null;
    }

    const { data, error } = await response.json();

    if (error) {
      console.error("[WorkspaceActions] Error revoking invitation:", error);
      store.set(workspaceInvitationsErrorAtom, String(error));
      return null;
    }

    store.set(workspaceInvitationsStatusAtom, "init");

    return data;
  } catch (error) {
    console.error("[WorkspaceActions] Error revoking invitation:", error);
    store.set(workspaceInvitationsErrorAtom, error instanceof Error ? error.message : String(error));
    return null;
  }
};

/**
 * Resend workspace invitation
 */
export const resendWorkspaceInvitation = async (jwt: string, workspaceId: string, invitationId: string) => {
  const store = getAtomStore();
  try {
    store.set(workspaceInvitationsErrorAtom, null);
    const response = await fetch(API_ROUTES.admin.workspaces.resendInvitation(workspaceId), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({ invitationId }),
    });

    if (!response.ok) {
      const msg = `HTTP error! status: ${response.status}`;
      console.error("[WorkspaceActions]", msg);
      store.set(workspaceInvitationsErrorAtom, msg);
      return null;
    }

    const { data, error } = await response.json();

    if (error) {
      console.error("[WorkspaceActions] Error resending invitation:", error);
      store.set(workspaceInvitationsErrorAtom, String(error));
      return null;
    }

    store.set(workspaceInvitationsStatusAtom, "init");

    return data;
  } catch (error) {
    console.error("[WorkspaceActions] Error resending invitation:", error);
    store.set(workspaceInvitationsErrorAtom, error instanceof Error ? error.message : String(error));
    return null;
  }
};

/**
 * Accept workspace invitation
 */
export const acceptWorkspaceInvitation = async (jwt: string, token: string) => {
  const _store = getAtomStore();
  try {
    const response = await fetch(API_ROUTES.auth.acceptInvitation, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({ token }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const { data } = await response.json();
    return data;
  } catch (error) {
    console.error("[WorkspaceActions] Error accepting invitation:", error);
    throw error;
  }
};
